// src/components/UnregisteredAlert.jsx
// Shown once per sign-in (per browser tab session) to the group's
// follow-up OWNERS (chosen by a Super Admin in User Management) while people
// in their group send faxes by email without a Fax Portal account. Nobody
// else gets it. Lists them and points to User Management.
import { useEffect, useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import { fetchUnregisteredSenders } from '../utils/api';

const SHOWN_KEY = 'faxPortal.unregisteredAlertShown';
const MAX_LISTED = 8;


export default function UnregisteredAlert({ onOpenList }) {
  const { user, can, viewAs, getToken } = useAuth();
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!user || viewAs || !can('users.view')) return;
    let shown = false;
    try { shown = window.sessionStorage.getItem(SHOWN_KEY) === user.email; } catch { /* ignore */ }
    if (shown) return;
    fetchUnregisteredSenders(getToken)
      .then(r => {
        const list = (r.data || []).filter(s => !s.removed);
        try { window.sessionStorage.setItem(SHOWN_KEY, user.email); } catch { /* ignore */ }
        if (r.isOwner && list.length) setData({ ...r, list });
      })
      .catch(() => {});
  }, [user, viewAs, can, getToken]);

  if (!data) return null;
  const close = () => setData(null);
  const canAdd = can('users.add');
  const n = data.list.length;

  return (
    <div style={S.overlay} onMouseDown={e => { if (e.target === e.currentTarget) close(); }}>
      <div style={S.modal} role="alertdialog" aria-modal="true" aria-labelledby="unreg-title" aria-describedby="unreg-text">
        <h2 id="unreg-title" style={S.title}>
          {n} {n === 1 ? 'person sends' : 'people send'} faxes by email without a Fax Portal account
        </h2>
        <p id="unreg-text" style={S.text}>
          Their faxes by email are refused until they're added to a client.
          {' '}{canAdd
            ? 'Please create accounts for the ones who should have access.'
            : 'Please ask your Admin to create accounts for the ones who should have access.'}
        </p>
        <ul style={S.list}>
          {data.list.slice(0, MAX_LISTED).map(s => (
            <li key={s.email} style={S.item}>
              <span style={S.email}>{s.email}</span>
              <span style={S.meta}>{s.faxes} fax{s.faxes === 1 ? '' : 'es'} in 90 days{s.requestStatus === 'pending' ? ' · asked for access' : ''}</span>
            </li>
          ))}
          {n > MAX_LISTED && <li style={S.more}>and {n - MAX_LISTED} more</li>}
        </ul>
        <div style={S.actions}>
          <button onClick={close} style={S.secondaryBtn}>Later</button>
          <button onClick={() => { close(); onOpenList(); }} style={S.primaryBtn}>
            {canAdd ? 'Create accounts' : 'See the list'}
          </button>
        </div>
      </div>
    </div>
  );
}

const S = {
  overlay: { position: 'fixed', inset: 0, background: 'rgb(var(--c-0f2823-rgb) / 0.45)', zIndex: 1150, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 },
  modal: { background: 'var(--c-ffffff)', borderRadius: 14, width: 'min(520px, 100%)', maxHeight: '85vh', overflowY: 'auto', padding: '22px 24px', boxShadow: '0 20px 60px rgb(var(--c-000000-rgb) / 0.25)', borderTop: '5px solid var(--c-ef6c00)' },
  title: { margin: '0 0 8px', fontSize: 19, fontWeight: 700, color: 'var(--t-bf360c)', lineHeight: 1.35 },
  text: { margin: '0 0 12px', fontSize: 14, color: 'var(--t-37474f)', lineHeight: 1.55 },
  list: { listStyle: 'none', margin: 0, padding: 0, border: '1px solid var(--c-eceff1)', borderRadius: 10 },
  item: { display: 'flex', justifyContent: 'space-between', gap: 12, padding: '8px 12px', borderBottom: '1px solid var(--c-f3f3f3)', flexWrap: 'wrap' },
  email: { fontSize: 14, fontWeight: 600, color: 'var(--t-263238)', overflowWrap: 'anywhere' },
  meta: { fontSize: 12, color: 'var(--t-78909c)' },
  more: { padding: '8px 12px', fontSize: 13, color: 'var(--t-607d8b)' },
  actions: { display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 },
  primaryBtn: { padding: '10px 18px', background: 'var(--c-ef6c00)', color: 'var(--t-ffffff)', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 14, fontWeight: 700, fontFamily: 'inherit' },
  secondaryBtn: { padding: '10px 16px', background: 'var(--c-ffffff)', color: 'var(--t-37474f)', border: '1.5px solid var(--c-cfd8dc)', borderRadius: 8, cursor: 'pointer', fontSize: 14, fontWeight: 600, fontFamily: 'inherit' },
};
